import axios, { AxiosPromise, AxiosRequestConfig } from "axios";
import { MotorcycleModelResponse } from "../../components/main/motorcycles/MotorcycleView";
import { VehicleOverviewResponse } from "../../components/main/overview/Overview";
import { TogglePartResponse } from "../../components/parts/PartsList";
import { CreateConfigurationResponse } from "../../components/configuration/ConfigurationView";
import {
	getConfigurationId,
	getLocaleQuery,
	getCulture
} from "../../utils/helpers";
import uuid from "uuid";
import { SaveDetailsData } from "../../components/dialogs/saveConfiguration";
import { ExportConfigurationValueNames } from "../../components/dialogs/exportConfiguration";
import { LoadConfigurationResponse } from "../../redux/reducers/load";
import { HistoryResponseItem } from "../../components/main/history/HistoryView";

export interface AddPartPayload {
	PartId: string;
	SelectedParts: string[];
	RemoveParts: string[];
}

export interface RemovePartPayload {
	PartId: string;
	RemoveParts: string[];
}

export function getModels(): AxiosPromise<MotorcycleModelResponse> {
	return axios.get<MotorcycleModelResponse>(
		`/api/posconfigurator/vehicles${getLocaleQuery()}`
	);
}

export function getOverview(
	modelId: string
): AxiosPromise<VehicleOverviewResponse> {
	return axios.get<VehicleOverviewResponse>(
		`/api/posconfigurator/vehicles/${modelId}${getLocaleQuery()}`
	);
}

export function addPart(
	payload: string | AddPartPayload
): AxiosPromise<TogglePartResponse> {
	let data: AddPartPayload;

	if (typeof payload === "string") {
		data = {
			PartId: payload,
			SelectedParts: [],
			RemoveParts: []
		};
	} else {
		data = { ...payload };
	}
	return axios.post<TogglePartResponse>(
		`/api/posconfigurator/configuration/${getConfigurationId()}/add-part${getLocaleQuery()}`,
		data
	);
}

export function removePart(
	payload: string | RemovePartPayload
): AxiosPromise<TogglePartResponse> {
	let data: RemovePartPayload;
	if (typeof payload === "string") {
		data = {
			PartId: payload,
			RemoveParts: []
		};
	} else {
		data = { ...payload };
	}
	return axios.post<TogglePartResponse>(
		`/api/posconfigurator/configuration/${getConfigurationId()}/remove-part${getLocaleQuery()}`,
		data
	);
}

export function createConfiguration(
	modelId: string
): AxiosPromise<CreateConfigurationResponse> {
	const data = {
		ModelId: modelId
	};
	const config: AxiosRequestConfig = {
		headers: {
			AnonymousID: uuid.v4()
		}
	};
	return axios.post<CreateConfigurationResponse>(
		`/api/posconfigurator/configuration/create${getLocaleQuery()}`,
		data,
		config
	);
}

export function deleteConfiguration(
	id: number | undefined = undefined
): AxiosPromise<any> {
	console.log("configuration reset request sent");
	id = id || getConfigurationId();
	return axios.delete(
		`/api/posconfigurator/configuration/${id}${getLocaleQuery()}`
	);
}

export function dismissUnsavedChanges(
	id: number | undefined = undefined
): AxiosPromise<any> {
	console.log("configuration reset request sent");
	id = id || getConfigurationId();
	return axios.post(
		`/api/posconfigurator/configuration/${id}/dismiss-unsaved-changes${getLocaleQuery()}`
	);
}

export function saveConfiguration(
	data: SaveDetailsData,
	saveAsNew: boolean
): AxiosPromise<any> {
	return axios.post<any>(
		`/api/posconfigurator/configuration/${getConfigurationId()}/save-configuration${getLocaleQuery()}`,
		{ ...data, SaveAsNew: saveAsNew }
	);
}

export function sendExport(
	data: ExportConfigurationValueNames
): AxiosPromise<any> {
	return axios.post<any>(
		`/api/posconfigurator/configuration/${getConfigurationId()}/send-export${getLocaleQuery()}`,
		data
	);
}

export function sendPdfSummary(email: string): AxiosPromise<any> {
	return axios.post<any>(
		`/api/posconfigurator/configuration/${getConfigurationId()}/send${getLocaleQuery()}`,
		{
			Email: email,
			CultureName: getCulture()
		}
	);
}

export function getConfiguration(
	configId: number
): AxiosPromise<LoadConfigurationResponse> {
	return axios.get<LoadConfigurationResponse>(
		`/api/posconfigurator/configuration/${configId}${getLocaleQuery()}`
	);
}

export function getHistory(): AxiosPromise<HistoryResponseItem> {
	return axios.get<HistoryResponseItem>(
		`/api/posconfigurator/dealer/history${getLocaleQuery()}`
	);
}
